/**
 * CyberBannerWeek2Slide
 * ──────────────────────────
 * Displays the Cybersecurity Awareness Month Week 2 promotional banner
 * as a full-bleed hero stage between the intro video and the CyberSecWeek2Slider.
 *
 * Behaviour:
 *  - Auto-advances after DISPLAY_DURATION ms (default 6 s).
 *  - Left arrow → back to video.
 *  - Right arrow / swipe-left → into CyberSecWeek2Slider.
 *  - Respects the same expiry date as CyberSecWeek2Slider so both disappear together.
 *  - Renders null when expired — Hero.jsx skips straight from video to main slides.
 */

import { useCallback, useEffect, useRef } from "react";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import "./CyberBannerSlide.css";

// Placeholder banner image - replace with actual banner when available
const bannerImg = "https://images.unsplash.com/photo-1563206767-5b18f218e8de?w=1600&q=80&fit=crop";

// Week 2 content runs from 14 Oct 2026 to 20 Oct 2026
const EXPIRY_DATE   = new Date("2026-10-20T23:59:59");
const DISPLAY_DURATION = 6000; // ms before auto-advancing to the cyber slider

export default function CyberBannerWeek2Slide({ onNext, onRequestVideo }) {
  const isExpired = Date.now() > EXPIRY_DATE.getTime();

  const onNextRef         = useRef(onNext);
  const onRequestVideoRef = useRef(onRequestVideo);
  useEffect(() => { onNextRef.current = onNext; }, [onNext]);
  useEffect(() => { onRequestVideoRef.current = onRequestVideo; }, [onRequestVideo]);

  // Auto-advance after DISPLAY_DURATION
  useEffect(() => {
    if (isExpired) return undefined;
    const t = setTimeout(() => { onNextRef.current?.(); }, DISPLAY_DURATION);
    return () => clearTimeout(t);
  }, [isExpired]);

  // Swipe support
  const touchStart = useRef(null);
  const handleTouchStart = (e) => { touchStart.current = e.touches?.[0]?.clientX ?? null; };
  const handleTouchEnd   = useCallback((e) => {
    if (touchStart.current === null) return;
    const delta = (e.changedTouches?.[0]?.clientX ?? touchStart.current) - touchStart.current;
    touchStart.current = null;
    if (Math.abs(delta) < 45) return;
    if (delta < 0) onNextRef.current?.();      // swipe-left  → next
    else           onRequestVideoRef.current?.(); // swipe-right → video
  }, []);

  // Keyboard
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowRight") onNextRef.current?.();
      if (e.key === "ArrowLeft")  onRequestVideoRef.current?.();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  if (isExpired) return null;

  return (
    <section
      className="cybanner-stage"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-label="Cybersecurity Awareness Month 2026 — Week 2 banner"
    >
      {/* Full-bleed banner image */}
      <img
        src={bannerImg}
        alt="Zetech University Cybersecurity Awareness Month 2026 — Week 2: Phishing & Social Engineering (banner placeholder)"
        className="cybanner-img"
        loading="eager"
        decoding="async"
        fetchPriority="high"
      />

      {/* Subtle bottom vignette so arrows/indicators stay legible */}
      <div className="cybanner-vignette" aria-hidden="true" />

      {/* Progress bar — thin line draining across the bottom */}
      <div className="cybanner-progress" aria-hidden="true">
        <div className="cybanner-progress-fill" style={{ animationDuration: `${DISPLAY_DURATION}ms` }} />
      </div>

      {/* Left arrow — back to video */}
      <button
        className="hero-arrow1 hero-arrow-left1 cybanner-arrow"
        onClick={() => onRequestVideoRef.current?.()}
        aria-label="Back to intro video"
      >
        <FaArrowLeft />
      </button>

      {/* Right arrow — into CyberSecWeek2Slider */}
      <button
        className="hero-arrow1 hero-arrow-right1 cybanner-arrow"
        onClick={() => onNextRef.current?.()}
        aria-label="Next: Cybersecurity tips"
      >
        <FaArrowRight />
      </button>

      {/* Expiry notice */}
      <div className="cybanner-expiry" aria-hidden="true">
        ⏱ Week 2 · ends 20 Oct 2026
      </div>

      {/* SR status */}
      <p className="hero-sr-status" aria-live="polite">
        Cybersecurity Awareness Month 2026 — Week 2 banner
      </p>
    </section>
  );
}
